import type { DeviceId, ReadingTypes } from '@/model/Device.model';
import InVivoError from '@/model/InVivoError';

/**
 * Detail payload for device:new-reading events.
 */
export interface DeviceNewReadingDetail {
  targetId: string;
  value: string;
  readingType: ReadingTypes;
  deviceTypeId: DeviceId;
  deviceTitle: string;
}

/**
 * Map of event types to their detail payloads.
 * Extend this type to add new device event types.
 */
type DeviceEventDetails = {
  'device:new-reading': DeviceNewReadingDetail;
};

/**
 * Subscription options for event listeners.
 * Requires an AbortSignal for cleanup.
 */
type SubscriptionOptions = {
  signal: AbortSignal;
} & Omit<AddEventListenerOptions, 'signal'>;

const eventBus = new EventTarget();
const eventBusProxy = new Proxy(eventBus, {
  get(target, prop) {
    if (prop === 'addEventListener' || prop === 'removeEventListener') {
      return target[prop].bind(target);
    }
    if (typeof prop === 'symbol') {
      return Reflect.get(target, prop);
    }
    throw new InVivoError(
      `DeviceEventBus: Method '${String(prop)}' is not accessible. Use DeviceEventBus.publish() or DeviceEventBus.subscribe().`,
      {
        slug: 'device-event-bus-invalid-access',
        level: 'error',
      }
    );
  },
  set() {
    throw new InVivoError('DeviceEventBus: Cannot modify the event bus directly.', {
      slug: 'device-event-bus-invalid-access',
      level: 'error',
    });
  },
});

/**
 * Type-safe event bus for device-related events.
 * Provides a secure, type-safe API for publishing and subscribing to device events.
 *
 * @example
 * ```typescript
 * // Subscribe to device readings
 * const controller = new AbortController();
 * DeviceEventBus.subscribe('device:new-reading', (event) => {
 *   console.log(event.detail.value);
 *   event.preventDefault(); // Suppress default handling
 * }, { signal: controller.signal });
 *
 * // Publish a reading
 * const event = DeviceEventBus.publish('device:new-reading', {
 *   targetId: 'weight',
 *   value: '25.3',
 *   readingType: 'number',
 *   deviceTypeId: 'dvc_abc123',
 * });
 *
 * if (!event.defaultPrevented) {
 *   // Handle reading normally
 * }
 *
 * // Cleanup
 * controller.abort();
 * ```
 */
export default class DeviceEventBus {
  /**
   * Publishes a cancelable event to the device event bus.
   * Subscribers can call event.preventDefault() to suppress default handling.
   *
   * @param type - The event type (e.g., 'device:new-reading')
   * @param detail - The event detail payload
   * @returns The dispatched CustomEvent, allowing inspection of defaultPrevented
   */
  static publish<T extends keyof DeviceEventDetails>(
    type: T,
    detail: DeviceEventDetails[T]
  ): CustomEvent<DeviceEventDetails[T]> {
    const event = new CustomEvent<DeviceEventDetails[T]>(type, {
      detail,
      cancelable: true,
    });
    eventBus.dispatchEvent(event);
    return event;
  }

  /**
   * Subscribes to events on the device event bus.
   * Always provide an AbortSignal for cleanup to prevent memory leaks.
   *
   * @param type - The event type to listen for
   * @param listener - Event handler function
   * @param options - Subscription options (must include signal for cleanup)
   */
  static subscribe<T extends keyof DeviceEventDetails>(
    type: T,
    listener: (event: CustomEvent<DeviceEventDetails[T]>) => void,
    options: SubscriptionOptions
  ): void {
    eventBus.addEventListener(type, listener as EventListener, options);
  }

  /**
   * Read-only proxy to the underlying EventTarget.
   * Only allows addEventListener and removeEventListener - all other operations throw.
   * Prefer using DeviceEventBus.subscribe() for type safety.
   */
  static get bus() {
    return eventBusProxy;
  }
}
